// src/components/GeminiChat.js
import React, { useState, useRef, useEffect } from 'react';
import { 
  FaRobot, 
  FaTimes, 
  FaComments, 
  FaRedo, 
  FaFilter, 
  FaChevronLeft, 
  FaChevronRight,
  FaUser,
  FaBriefcase,
  FaFileAlt,
  FaHome,
  FaRocket,
  FaSmile,
  FaGlobeAmericas,
  FaGraduationCap,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaBalanceScale,
  FaSearch,
  FaPaperPlane,
  FaSync,
  FaExclamationTriangle,
  FaFolderOpen
} from 'react-icons/fa';
import { geminiService } from '../api'; 
import './GeminiChat.css';

const GeminiChat = ({ onUseResponse, context }) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [apiStatus, setApiStatus] = useState('checking');
    const [isOpen, setIsOpen] = useState(false);
    const [showCategories, setShowCategories] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const messagesEndRef = useRef(null);
    const categoriesScrollRef = useRef(null);

    // Catégories avec questions prédéfinies
    const categories = {
        'emploi': {
            title: 'Emploi',
            icon: <FaBriefcase className="category-icon" />,
            questions: [
                "Comment trouver un job étudiant en France ?",
                "Quels sont les secteurs qui recrutent le plus pour les étudiants ?",
                "Comment rédiger un CV français efficace ?",
                "Quelle est la durée légale du travail pour un étudiant ?",
                "Comment négocier son salaire en tant qu'étudiant ?",
                "Quelles sont les plateformes pour trouver des jobs étudiants ?",
                "Comment concilier études et travail ?"
            ]
        },
        'situation_administrative': {
            title: 'Administration',
            icon: <FaFileAlt className="category-icon" />,
            questions: [
                "Quelles sont les démarches pour renouveler mon titre de séjour ?",
                "Comment obtenir une carte de séjour étudiant ?",
                "Quels documents pour mon inscription à l'université ?",
                "Comment régulariser ma situation en cas de retard ?",
                "Quelles sont les obligations pour maintenir mon statut d'étudiant ?",
                "Comment obtenir une attestation de scolarité ?",
                "Que faire en cas de perte de mes documents ?"
            ]
        },
        'logement': {
            title: 'Logement',
            icon: <FaHome className="category-icon" />,
            questions: [
                "Comment trouver un logement étudiant pas cher ?",
                "Quelles sont les aides au logement pour étudiants ?",
                "Comment éviter les arnaques locatives ?",
                "Quels sont mes droits en tant que locataire ?",
                "Comment constituer un dossier de location solide ?",
                "Quelles résidences universitaires me conseilles-tu ?",
                "Comment résoudre un conflit avec mon propriétaire ?"
            ]
        },
        'entrepreneuriat': {
            title: 'Entrepreneuriat',
            icon: <FaRocket className="category-icon" />,
            questions: [
                "Comment créer une entreprise en tant qu'étudiant ?",
                "Quels statuts juridiques pour un étudiant-entrepreneur ?",
                "Comment financer mon projet entrepreneurial ?",
                "Quelles aides pour les jeunes créateurs d'entreprise ?",
                "Comment concilier études et entrepreneuriat ?",
                "Où trouver un incubateur étudiant ?",
                "Comment développer mon réseau entrepreneurial ?"
            ]
        },
        'bien_etre_integration': {
            title: 'Bien-être',
            icon: <FaSmile className="category-icon" />,
            questions: [
                "Comment surmonter le mal du pays ?",
                "Où rencontrer d'autres étudiants internationaux ?",
                "Comment améliorer mon français rapidement ?",
                "Quelles activités pour m'intégrer en France ?",
                "Où trouver un soutien psychologique étudiant ?",
                "Comment gérer le stress des études ?",
                "Quelles associations étudiantes rejoindre ?"
            ]
        },
        'tourisme': {
            title: 'Tourisme',
            icon: <FaGlobeAmericas className="category-icon" />,
            questions: [
                "Quels lieux visiter en France avec un budget étudiant ?",
                "Comment voyager pas cher en Europe ?",
                "Quelles réductions pour les étudiants dans les musées ?",
                "Où partir en weekend pas cher depuis la France ?",
                "Comment organiser un road-trip étudiant ?",
                "Quels festivals étudiants recommandes-tu ?",
                "Comment trouver des hébergements gratuits ou low-cost ?"
            ]
        },
        'projet_etude': {
            title: 'Études',
            icon: <FaGraduationCap className="category-icon" />,
            questions: [
                "Comment choisir mon master en France ?",
                "Quelles spécialités sont porteuses pour l'emploi ?",
                "Comment rédiger une lettre de motivation percutante ?",
                "Quelles sont les meilleures écoles dans mon domaine ?",
                "Comment obtenir une bourse d'études en France ?",
                "Comment réussir mon entretien d'admission ?",
                "Quels sont les débouchés de ma formation ?"
            ]
        },
        'evenements_pro': {
            title: 'Événements PRO',
            icon: <FaCalendarAlt className="category-icon" />,
            questions: [
                "Quels salons recrutement pour étudiants ?",
                "Comment préparer un forum emploi ?",
                "Où trouver des conférences professionnelles gratuites ?",
                "Comment réseauter efficacement en événement pro ?",
                "Quels workshops pour développer mes compétences ?",
                "Comment trouver des stages via les événements ?",
                "Quelles applications pour suivre l'agenda des événements pros ?"
            ]
        },
        'aide_financiere': {
            title: 'Finance',
            icon: <FaMoneyBillWave className="category-icon" />,
            questions: [
                "Quelles bourses pour étudiants internationaux ?",
                "Comment obtenir les APL ?",
                "Quelles aides pour le transport étudiant ?",
                "Comment financer mes études en France ?",
                "Quels prêts étudiants avantageux ?",
                "Comment réduire mes frais de scolarité ?",
                "Quelles aides d'urgence pour étudiants ?"
            ]
        },
        'accompagnement_juridique': {
            title: 'Juridique',
            icon: <FaBalanceScale className="category-icon" />,
            questions: [
                "Quels sont mes droits en tant qu'étudiant étranger ?",
                "Comment contester une décision administrative ?",
                "Où trouver une aide juridique gratuite ?",
                "Comment résoudre un litige avec mon université ?",
                "Quelles démarches en cas de problème avec mon employeur ?",
                "Comment me défendre en cas de discrimination ?",
                "Quels recours en cas de refus de titre de séjour ?"
            ]
        }
    };

    // Vérifier la clé API au chargement
    useEffect(() => {
        checkApiKey();
    }, []);

    const checkApiKey = async () => {
        const validation = await geminiService.validateApiKey();
        if (validation.valid) {
            setApiStatus('valid');
            setError('');
        } else {
            setApiStatus('invalid');
            setError(validation.message);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollCategories = (direction) => {
        if (categoriesScrollRef.current) {
            const scrollAmount = 200;
            const newPosition = direction === 'right' 
                ? scrollPosition + scrollAmount
                : scrollPosition - scrollAmount;
            
            setScrollPosition(Math.max(0, newPosition));
            categoriesScrollRef.current.scrollLeft = newPosition;
        }
    };

    const sendMessage = async (e) => {
        e?.preventDefault();
        
        if (!inputMessage.trim() || isLoading) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: inputMessage,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);
        setError('');

        try {
            // Ajouter le contexte si fourni
            const finalMessage = context ? `${context}\n\n${inputMessage}` : inputMessage;
            
            const response = await geminiService.sendMessage(finalMessage);
            
            if (response.success) {
                const aiMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: response.text,
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };
                setMessages(prev => [...prev, aiMessage]);
            } else {
                setError(response.error);
            }
        } catch (error) {
            setError('Erreur inattendue: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const resetChat = () => {
        geminiService.resetChat();
        setMessages([]);
        setError('');
        setSelectedCategory(null);
        setShowCategories(false);
        setScrollPosition(0);
    };

    const handleQuickPrompt = (prompt) => {
        setInputMessage(prompt);
        setTimeout(() => {
            sendMessage();
        }, 100);
    };

    const handleCategorySelect = (categoryKey) => {
        setSelectedCategory(categoryKey);
        setShowCategories(false);
    };

    const handleCategoryQuestion = (question) => {
        setInputMessage(question);
        setTimeout(() => {
            sendMessage();
        }, 100);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(e);
        }
    };

    const handleUseResponse = () => {
        if (onUseResponse && messages.length > 0) {
            const lastAIMessage = messages.filter(msg => msg.role === 'assistant').pop();
            if (lastAIMessage) {
                onUseResponse(lastAIMessage.content);
                setIsOpen(false);
            }
        }
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setShowCategories(false);
            setSelectedCategory(null);
            setScrollPosition(0);
        }
    };

    const toggleCategories = () => {
        setShowCategories(!showCategories);
        setScrollPosition(0);
    };

    // Si le chat n'est pas ouvert, afficher seulement l'icône flottante
    if (!isOpen) {
        return (
            <button 
                className="floating-chat-button"
                onClick={toggleChat}
                title="Ouvrir l'assistant IA"
            >
                <FaRobot className="chat-icon" />
                <span className="notification-dot"></span>
            </button>
        );
    }

    return (
        <>
            {/* Overlay */}
            <div className="chat-overlay" onClick={toggleChat}></div>
            
            {/* Chat Container */}
            <div className="gemini-chat-popup">
                <div className="chat-header">
                    <div className="header-info">
                        <h2>
                            <FaRobot className="icon" />
                            Assistant Gemini
                        </h2>
                        <div className={`api-status ${apiStatus}`}>
                            {apiStatus === 'checking' && <><FaSearch /> Vérification...</>}
                            {apiStatus === 'valid' && <><FaRobot /> En ligne</>}
                            {apiStatus === 'invalid' && <><FaExclamationTriangle /> Hors ligne</>}
                        </div>
                    </div>
                    <div className="header-actions">
                        <button 
                            onClick={toggleCategories}
                            className={`filter-btn ${showCategories ? 'active' : ''}`}
                            title="Filtrer par catégorie"
                        >
                            <FaFilter className="icon" />
                        </button>
                        {onUseResponse && messages.length > 0 && (
                            <button 
                                onClick={handleUseResponse}
                                className="use-response-btn"
                                title="Utiliser cette réponse"
                            >
                                <FaComments className="icon" />
                                Utiliser
                            </button>
                        )}
                        <button onClick={resetChat} className="reset-btn" title="Nouvelle conversation">
                            <FaRedo className="icon" />
                        </button>
                        <button onClick={toggleChat} className="close-btn" title="Fermer">
                            <FaTimes className="icon" />
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="error-message">
                        <span>{error}</span>
                        <button onClick={checkApiKey} className="retry-btn">
                            <FaSync /> Réessayer
                        </button>
                    </div>
                )}

                {/* Panneau des catégories horizontales */}
                {showCategories && (
                    <div className="categories-panel-horizontal">
                        <div className="categories-header">
                            <h3><FaFolderOpen /> Choisissez une catégorie</h3>
                            <p>Faites défiler pour voir toutes les thématiques</p>
                        </div>
                        <div className="categories-scroll-container">
                            <button 
                                className="scroll-btn scroll-left"
                                onClick={() => scrollCategories('left')}
                                disabled={scrollPosition === 0}
                            >
                                <FaChevronLeft />
                            </button>
                            
                            <div className="categories-scroll" ref={categoriesScrollRef}>
                                <div className="categories-list">
                                    {Object.entries(categories).map(([key, category]) => (
                                        <button
                                            key={key}
                                            onClick={() => handleCategorySelect(key)}
                                            className={`category-btn-horizontal ${selectedCategory === key ? 'selected' : ''}`}
                                        >
                                            <span className="category-icon-wrapper">
                                                {category.icon}
                                            </span>
                                            <span className="category-title">{category.title}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            <button 
                                className="scroll-btn scroll-right"
                                onClick={() => scrollCategories('right')}
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                )}

                {/* Questions de la catégorie sélectionnée */}
                {selectedCategory && !showCategories && (
                    <div className="category-questions">
                        <div className="questions-header">
                            <button 
                                onClick={() => setShowCategories(true)}
                                className="back-btn"
                                title="Retour aux catégories"
                            >
                                <FaChevronLeft /> Retour
                            </button>
                            <h4>
                                {categories[selectedCategory].icon} 
                                {categories[selectedCategory].title}
                            </h4>
                        </div>
                        <div className="questions-list">
                            {categories[selectedCategory].questions.map((question, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleCategoryQuestion(question)}
                                    className="question-btn"
                                    disabled={isLoading || apiStatus !== 'valid'}
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="messages-container">
                    {messages.length === 0 && !selectedCategory && !showCategories && (
                        <div className="empty-state">
                            <h3><FaComments /> Assistant IA Étudiant</h3>
                            <p>Comment puis-je vous aider dans votre vie étudiante en France ?</p>
                            <button 
                                onClick={toggleCategories}
                                className="browse-categories-btn"
                            >
                                <FaFolderOpen /> Parcourir les catégories
                            </button>
                        </div>
                    )}

                    {messages.map((message) => (
                        <div key={message.id} className={`message ${message.role}`}>
                            <div className="message-avatar">
                                {message.role === 'user' ? <FaUser /> : <FaRobot />}
                            </div>
                            <div className="message-content">
                                <div className="message-text">{message.content}</div>
                                <div className="message-time">{message.timestamp}</div>
                            </div>
                        </div>
                    ))}

                    {isLoading && (
                        <div className="message assistant">
                            <div className="message-avatar"><FaRobot /></div>
                            <div className="message-content">
                                <div className="typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                </div>

                <form onSubmit={sendMessage} className="message-form">
                    <div className="input-container">
                        <input
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={
                                apiStatus === 'valid' 
                                ? "Tapez votre message ou choisissez une catégorie..." 
                                : "Assistant en cours de configuration..."
                            }
                            disabled={isLoading || apiStatus !== 'valid'}
                            className="message-input"
                            autoFocus
                        />
                        <button 
                            type="submit" 
                            disabled={isLoading || !inputMessage.trim() || apiStatus !== 'valid'}
                            className="send-button"
                            title="Envoyer le message"
                        >
                            {isLoading ? <FaSync className="spin" /> : <FaPaperPlane />}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default GeminiChat;